import React from 'react';
import { UserProvider } from './UserContext';
import { CartProvider } from './contexts/CartContext';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// --- Legacy / production screens (Supabase auth, cart, checkout, family) ---
import LoginScreen from './screens/LoginScreen';
import CreateAccountScreen from './screens/CreateAccountScreen';
import DashboardScreen from './screens/DashboardScreen';
import ChatScreen from './screens/ChatScreen';
import ModifyPreferencesScreen from './screens/ModifyPreferencesScreen';
import EditProfileScreen from './screens/EditProfileScreen';
import AboutScreen from './screens/AboutScreen';
import AddRestaurantScreen from './screens/AddRestaurantScreen';
import LandingScreen from './screens/LandingScreen';
import HowItWorksScreen from './screens/HowItWorksScreen';
import PricingScreen from './screens/PricingScreen';
import ServerDashboardScreen from './screens/ServerDashboardScreen';
import MenuManagerScreen from './screens/MenuManagerScreen';
import RestaurantDiscoveryScreen from './screens/RestaurantDiscoveryScreen';
import CheckoutScreen from './screens/CheckoutScreen';
import OrderStatusScreen from './screens/OrderStatusScreen';
import OrderHistoryScreen from './screens/OrderHistoryScreen';
import FamilyManagementScreen from './screens/FamilyManagementScreen';
import MyTableScreen from './screens/MyTableScreen';
import TableQuestionsScreen from './screens/TableQuestionsScreen';
import SafetySignalsScreen from './screens/SafetySignalsScreen';
import TermsPage from './screens/TermsPage';
import PrivacyPolicyPage from './screens/PrivacyPolicyPage';
import Layout from './NavBarLayout';

// --- Unified redesign screens (brand refresh, under /unified/*) ---
// These live OUTSIDE the legacy MUI Layout so they get the new AppShell.
// `/` flips to UnifiedLanding; the old LandingScreen is preserved at
// /legacy/v1-landing for one-line rollback.
import UnifiedLanding from './unified/screens/UnifiedLanding';
import UnifiedDiscover from './unified/screens/UnifiedDiscover';
import UnifiedDashboard from './unified/screens/UnifiedDashboard';
import UnifiedPricing from './unified/screens/UnifiedPricing';
import UnifiedForRestaurants from './unified/screens/UnifiedForRestaurants';
import UnifiedHowItWorks from './unified/screens/UnifiedHowItWorks';
import UnifiedAbout from './unified/screens/UnifiedAbout';
import UnifiedOnboarding from './unified/screens/UnifiedOnboarding';
import UnifiedChat from './unified/screens/UnifiedChat';
import UnifiedRestaurantDetail from './unified/screens/UnifiedRestaurantDetail';
import UnifiedMyTable from './unified/screens/UnifiedMyTable';
import UnifiedNotifications from './unified/screens/UnifiedNotifications';
import UnifiedSafetySignals from './unified/screens/UnifiedSafetySignals';
import UnifiedTableQuestions from './unified/screens/UnifiedTableQuestions';
import UnifiedOrderHistory from './unified/screens/UnifiedOrderHistory';
import UnifiedOrderStatus from './unified/screens/UnifiedOrderStatus';
import UnifiedOwnerDashboard from './unified/screens/UnifiedOwnerDashboard';
import UnifiedProfile from './unified/screens/UnifiedProfile';
import UnifiedAllergies from './unified/screens/UnifiedAllergies';
import UnifiedAddReview from './unified/screens/UnifiedAddReview';
import UnifiedNotFound from './unified/screens/UnifiedNotFound';
import { AuthModalProvider } from './features/auth/AuthModalContext';
import AuthModal from './features/auth/AuthModal';
import { I18nProvider } from './features/i18n';

// Initialize Stripe (production payment provider — keeps cart/checkout working)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);


const App = () => {
  return (
    <UserProvider>
      <I18nProvider>
      <CartProvider>
        <Elements stripe={stripePromise}>
          <Router>
            <AuthModalProvider>
              <AuthModal />
              <Routes>
                {/* =============================================================
                    GO-LIVE: `/` renders the unified-redesign landing. The old
                    MUI landing is preserved at `/legacy/v1-landing` so rollback
                    is flipping this one line. Unified routes live outside the
                    MUI Layout — they render their own AppShell.
                    ============================================================= */}
                <Route path="/" element={<UnifiedLanding />} />
                <Route path="/legacy/v1-landing" element={<Layout><LandingScreen /></Layout>} />

                {/* Unified redesign surface (/unified/*) — same set of screens
                    the new landing references via its CTAs. */}
                <Route path="/unified" element={<UnifiedLanding />} />
                <Route path="/unified/discover" element={<UnifiedDiscover />} />
                <Route path="/unified/dashboard" element={<UnifiedDashboard />} />
                <Route path="/unified/pricing" element={<UnifiedPricing />} />
                <Route path="/unified/restaurant-pricing" element={<UnifiedPricing />} />
                <Route path="/unified/for-restaurants" element={<UnifiedForRestaurants />} />
                <Route path="/unified/how-it-works" element={<UnifiedHowItWorks />} />
                <Route path="/unified/about" element={<UnifiedAbout />} />
                <Route path="/unified/onboarding" element={<UnifiedOnboarding />} />
                <Route path="/unified/chat" element={<UnifiedChat />} />
                <Route path="/unified/chat/:id" element={<UnifiedChat />} />
                <Route path="/unified/restaurant/:id" element={<UnifiedRestaurantDetail />} />
                <Route path="/unified/my-table" element={<UnifiedMyTable />} />
                <Route path="/unified/notifications" element={<UnifiedNotifications />} />
                <Route path="/unified/safety-signals" element={<UnifiedSafetySignals />} />
                <Route path="/unified/table-questions" element={<UnifiedTableQuestions />} />
                <Route path="/unified/orders" element={<UnifiedOrderHistory />} />
                <Route path="/unified/orders/:id" element={<UnifiedOrderStatus />} />
                <Route path="/unified/order-status/:orderId" element={<UnifiedOrderStatus />} />
                <Route path="/unified/owner-dashboard" element={<UnifiedOwnerDashboard />} />
                <Route path="/unified/profile" element={<UnifiedProfile />} />
                <Route path="/unified/allergies" element={<UnifiedAllergies />} />
                <Route path="/unified/add-review" element={<UnifiedAddReview />} />
                <Route path="/unified/*" element={<UnifiedNotFound />} />

                {/* =============================================================
                    Legacy / production surface — unchanged from droplet-preserve.
                    These are the Supabase-auth-backed routes that have been live
                    on chatwithmenu.com and should keep working as-is.
                    ============================================================= */}
                <Route path="/login" element={<Layout><LoginScreen /></Layout>} />
                <Route path="/create-account" element={<Layout><CreateAccountScreen /></Layout>} />
                <Route path="/about" element={<Layout><AboutScreen /></Layout>} />
                <Route path="/dashboard" element={<Layout><DashboardScreen /></Layout>} />
                <Route path="/chat" element={<Layout><ChatScreen /></Layout>} />
                <Route path="/modify-preferences" element={<Layout><ModifyPreferencesScreen /></Layout>} />
                <Route path="/edit-profile" element={<Layout><EditProfileScreen /></Layout>} />
                <Route path="/add-restaurant" element={<Layout><AddRestaurantScreen /></Layout>} />
                <Route path="/how-it-works" element={<Layout><HowItWorksScreen /></Layout>} />
                <Route path="/pricing" element={<Layout><PricingScreen /></Layout>} />
                <Route path="/server-dashboard" element={<Layout><ServerDashboardScreen /></Layout>} />
                <Route path="/menu-manager" element={<Layout><MenuManagerScreen /></Layout>} />
                <Route path="/discover" element={<Layout><RestaurantDiscoveryScreen /></Layout>} />
                <Route path="/checkout" element={<Layout><CheckoutScreen /></Layout>} />
                <Route path="/order-status/:orderId" element={<Layout><OrderStatusScreen /></Layout>} />
                <Route path="/order-history" element={<Layout><OrderHistoryScreen /></Layout>} />
                <Route path="/family-management" element={<Layout><FamilyManagementScreen /></Layout>} />
                <Route path="/my-table" element={<Layout><MyTableScreen /></Layout>} />
                <Route path="/table-questions" element={<Layout><TableQuestionsScreen /></Layout>} />
                <Route path="/safety-signals" element={<Layout><SafetySignalsScreen /></Layout>} />
                <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
                <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
              </Routes>
            </AuthModalProvider>
          </Router>
        </Elements>
      </CartProvider>
      </I18nProvider>
    </UserProvider>
  );
};

export default App;
